// A spirit's powers (issue 1172; docs/MRCS/shadowrun-3.md §15), under its token's Actions: every power its entry lists,
// as the server read them, each with its rule. A power with a test of its own (Fear, Binding, Noxious Breath) is a
// button that uses it on the aimed target: the server reads the power, the spirit's and the target's sheets, throws the
// test and writes the result with the rule for the DM to settle — privately when the spirit is hidden. Its melee power
// (Engulf) is a weapon of the spirit in the Melee attack form; the rest are rules the DM settles. Only for whoever
// controls the spirit (its conjurer's player, or the DM); nothing here decides a number.
import { useEffect, useState } from 'react';
import { fetchTests, type SpiritPowerRow } from '../../entries';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { BTN } from '../ui';

interface Thing { id: string; kind: string; name?: string; mine?: boolean; character?: string | null; entry?: unknown }

const int = (v: string): number | null => (v.trim() !== '' && Number.isInteger(Number(v)) && Number(v) >= 1 ? Number(v) : null);

export function TokenPowers({ o, aimed }: { o: Thing; aimed: Thing | undefined }) {
  const { act, setNotice, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const [powers, setPowers] = useState<SpiritPowerRow[]>([]);
  const [typed, setTyped] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const key = `${o.id} ${JSON.stringify(o.entry ?? null)}`;
  useEffect(() => {
    setPowers([]);
    if (!o.entry || !systemRules.conjuring) return undefined;
    let live = true;
    void fetchTests(campaignId, o.id, scene?.id ?? null).then((a) => { if (live) setPowers(a?.powers ?? []); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, campaignId, systemRules.conjuring]);
  if (!powers.length) return null;
  const use = (p: SpiritPowerRow) => {
    if (!aimed) return;
    const rating = int(typed);
    act({ kind: 'spirit.power', spirit: o.id, power: p.ref, target: aimed.id, ...(rating ? { rating } : {}) } as never,
      (reply) => { if (!reply.ok) setNotice(reply.reason ?? `${p.name} could not be used.`); });
  };
  return (
    <div className="space-y-1" data-testid="spirit-powers">
      <div className="flex flex-wrap items-center gap-1">
        <span className="font-semibold text-amber-300">Powers</span>
        <label className="flex items-center gap-1 text-stone-400">target's resistance, if its sheet has none
          <input aria-label="Target's resistance" type="number" min={1} max={60} className="w-12 rounded bg-stone-800 px-1 py-0.5" value={typed} onChange={(e) => setTyped(e.target.value)} />
        </label>
      </div>
      <div className="flex flex-wrap gap-1">
        {powers.map((p) => (p.test
          ? <button key={p.ref} type="button" className={`${BTN} border-amber-700`} disabled={!aimed} title={`${p.effect}${aimed ? '' : ' (Choose a target first: here, or Target in its menu.)'}`} onClick={() => use(p)}>{p.name}</button>
          : <button key={p.ref} type="button" className={`${BTN} border-stone-600`} aria-expanded={open === p.ref} onClick={() => setOpen(open === p.ref ? null : p.ref)}>{p.name}</button>))}
      </div>
      {open && (() => {
        const p = powers.find((x) => x.ref === open);
        if (!p) return null;
        const how = p.roll === 'melee' ? ' It is a melee attack: choose it as the spirit\'s weapon in the Melee attack form.' : p.roll === 'spell' ? ` Cast as its spell${p.spell ? ` (${p.spell.replace(/-/g, ' ')})` : ''}: choose this spirit as the caster in the dice bag's Cast spell…, where it is listed at its Force, with no drain.` : ' The DM settles it.';
        return <p className="text-stone-300" data-testid="power-rule"><span className="font-semibold">{p.name}</span>{[p.action, p.range, p.duration].filter(Boolean).length ? ` (${[p.action, p.range, p.duration].filter(Boolean).join(', ')})` : ''}: {p.effect}{how}</p>;
      })()}
    </div>
  );
}
